import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import AppLayout from "./components/Layout/AppLayout";

// Pages
import LoginPage                  from "./pages/Login/LoginPage";
const DashboardPage = React.lazy(() => import("./pages/Dashboard/DashboardPage"));
import HomePage                   from "./pages/Home/HomePage";
import MealEntryPage               from "./pages/MealEntry/MealEntryPage";
import AllDataPage                 from "./pages/AllData/AllDataPage";
import ReceiptPage                 from "./pages/Receipt/ReceiptPage";
import SummaryDirectoratesPage     from "./pages/Summary/SummaryDirectoratesPage";
import SummaryGovernoratesPage     from "./pages/Summary/SummaryGovernoratesPage";
import SummaryDetailPage           from "./pages/Summary/SummaryDetailPage";
import HeadcountPage               from "./pages/Headcount/HeadcountPage";
import SettingsPage                from "./pages/Settings/SettingsPage";
import DictionariesPage            from "./pages/Dictionaries/DictionariesPage";
import UsersPage                   from "./pages/Users/UsersPage";
import AuditPage                   from "./pages/Audit/AuditPage";
import ProfilePage                 from "./pages/Profile/ProfilePage";
import DirectorateScopeRedirect    from "./components/DirectorateScopeRedirect";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000 },
  },
});

function PrivateRoute({ children, roles }: { children: React.ReactNode; roles?: string[] }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="flex items-center justify-center h-screen text-gray-400">جاري التحميل...</div>;
  if (!user)   return <Navigate to="/login" replace />;
  // كلمة مرور مؤقتة من المدير ⇒ يجب تغييرها قبل أي شيء آخر
  if (user.mustChangePwd && location.pathname !== "/profile") return <Navigate to="/profile" replace />;
  if (roles && !roles.includes(user.role)) return (
    <AppLayout>
      <div className="alert-error">ليس لديك صلاحية للوصول إلى هذه الصفحة</div>
    </AppLayout>
  );
  return <AppLayout>{children}</AppLayout>;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen text-gray-400">جاري التحميل...</div>;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />

      <Route path="/" element={<PrivateRoute><HomePage /></PrivateRoute>} />
      <Route path="/dashboard" element={
        <PrivateRoute>
          <React.Suspense fallback={<div className="p-10 text-center text-sand-400">جاري تحميل لوحة المعلومات...</div>}>
            <DashboardPage />
          </React.Suspense>
        </PrivateRoute>
      } />
      <Route path="/meal-entry" element={<PrivateRoute roles={["DATA_ENTRY","REVIEWER","SYSTEM_ADMIN"]}><MealEntryPage /></PrivateRoute>} />
      <Route path="/all-data"   element={<PrivateRoute><AllDataPage /></PrivateRoute>} />
      <Route path="/receipt"    element={<PrivateRoute><ReceiptPage /></PrivateRoute>} />

      <Route path="/summary/directorates"       element={<PrivateRoute><SummaryDirectoratesPage /></PrivateRoute>} />
      <Route path="/summary/governorates"        element={<PrivateRoute><SummaryGovernoratesPage /></PrivateRoute>} />
      <Route path="/summary/directorate/:id"     element={<PrivateRoute><SummaryDetailPage /></PrivateRoute>} />

      <Route path="/headcount"           element={<PrivateRoute roles={["REVIEWER","SYSTEM_ADMIN"]}><HeadcountPage /></PrivateRoute>} />
      <Route path="/headcount/*"         element={<Navigate to="/headcount" replace />} />

      <Route path="/settings"     element={<PrivateRoute roles={["SYSTEM_ADMIN"]}><SettingsPage /></PrivateRoute>} />
      <Route path="/profile"     element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
      <Route path="/dictionaries" element={<PrivateRoute roles={["SYSTEM_ADMIN"]}><DictionariesPage /></PrivateRoute>} />
      <Route path="/users"        element={<PrivateRoute roles={["SYSTEM_ADMIN"]}><UsersPage /></PrivateRoute>} />
      <Route path="/audit"        element={<PrivateRoute roles={["SYSTEM_ADMIN"]}><AuditPage /></PrivateRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <DirectorateScopeRedirect>
            <AppRoutes />
          </DirectorateScopeRedirect>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
